import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { createPortal } from 'react-dom';
import { InputField, LoaderOverlay, Modal } from '../../../components';
import { HeaderContext, UserContext } from '../../../utils/context';
import {
  SubmitText,
  ForgotPasswordText,
  WelcomeLabelText,
  WelcomeText,
  // RegisteredText,
  // SignUpText,
} from '../../data/constants';
import API from '../../../services/api-middleware';
import { authFormProps } from './auth-form.props';
import { PasswordEyeIconGray, PasswordEyeIconGraySlash } from '../../../assets/images';
import './auth-form.css';
import { redirectToFirstPermittedModule } from '../../../services';

const baseUrl = process.env.REACT_APP_USER_SERVER_URL;

function AuthForm({
  setIsLoggedIn,
  setForgotPasswordOpen,
  // setSignUpPageOpen
}: authFormProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUserName } = useContext(HeaderContext);
  const { user, setUser } = useContext(UserContext);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [userdata, setUserdata] = useState<{
    name?: string;
    email?: string;
    password?: string;
  }>({
    name: '',
    email: '',
    password: '',
  });
  const form = 'Login';
  const [errorInfo, setErrorInfo] = useState<{
    name: string;
    email: string;
    password: string;
  }>({
    name: '',
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);
  const [, setSuccess] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<{
    name: string;
    email: string;
  }>({
    name: '',
    email: '',
  });
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [, setError] = useState<boolean>(true);
  const [sessionExpired, setSessionExpired] = useState<boolean>(false);

  let errors: {
    name?: string;
    email?: string;
    password?: string;
  } = { name: '', email: '', password: '' };

  useEffect(() => {
    const session = localStorage.getItem('sessionExpired');
    if (session) {
      setSessionExpired(JSON.parse(session));
    }
  }, [sessionExpired]);

  useEffect(() => {
    if (form === 'Login') {
      setError(!false);
    }
  }, [errorInfo]);

  useEffect(() => {
    setErrorInfo({
      name: '',
      email: '',
      password: '',
    });
    setUserdata({
      name: '',
      email: '',
      password: '',
    });
    document.querySelectorAll('input').forEach((input) => {
      // eslint-disable-next-line no-param-reassign
      input.value = '';
    });
    setError(true);
  }, [form]);

  // Adding Input values to the userdata
  const addUserData = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUserdata({
      ...userdata,
      [event.target.name]: event.target.value,
    });
  };

  // Handling change and errors
  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = e.target;
    if (name === 'name') {
      if (value.length) {
        if (!value[0].match(/^[A-Za-z]+$/)) {
          errors = {
            ...errors,
            [name]: `${name.replace('_', ' ')} can start with alphabets only`,
          };
          setUserdata({ ...userdata, [name]: '' });
          setError(true);
        } else if (value.length > 20) {
          errors = {
            ...errors,
            [name]: `${name.replace('_', ' ')} allows only 20 characters`,
          };
          setUserdata({ ...userdata, [name]: '' });
          setError(true);
        } else {
          delete errors?.[name];
          setUserdata({ ...userdata, [name]: value });
          setError(false);
          addUserData(e);
        }
      } else if (value === '') {
        errors = {
          ...errors,
          [name]: `Please enter your ${name.replace('_', ' ')}`,
        };
        setUserdata({ ...userdata, [name]: '' });
        setError(true);
      }
    } else if (name === 'email') {
      if (value.length > 2) {
        const emailRegex =
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        if (!value.match(emailRegex)) {
          errors = { ...errors, email: 'Please enter a valid email address' };
          setUserdata({ ...userdata, email: '' });
          setError(true);
        } else {
          delete errors?.email;
          setUserdata({ ...userdata, email: value });
          setError(false);
          addUserData(e);
        }
      } else {
        errors = { ...errors, email: 'Please enter your email' };
        setUserdata({ ...userdata, email: '' });
        setError(true);
      }
    } else if (name === 'password') {
      if (value.length) {
        if (value.length < 6) {
          errors = {
            ...errors,
            password: 'Password should be between 6 characters long',
          };
          setUserdata({ ...userdata, password: '' });
          setError(true);
        } else {
          delete errors?.password;
          setUserdata({ ...userdata, password: value });
          setError(false);
          addUserData(e);
        }
      } else {
        errors = { ...errors, password: 'Please enter your password' };
        setUserdata({ ...userdata, password: '' });
        setError(true);
      }
    }

    setErrorInfo({ ...errorInfo, [name]: errors[name as keyof typeof errors] || '' });
  };

  // Form handling
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    let errorsVal = true;
    localStorage.setItem('sessionExpired', 'false');
    localStorage.setItem('activeTab', '0');
    const formData = userdata;
    let endpoint = ''; // Signup API
    // Validate formData based on Form selected
    if (form === 'Login') {
      endpoint = '/login'; // Login API
      delete formData.name;
      if (errorInfo.email === '' && errorInfo.password === '') {
        errorsVal = false;
      }
    }

    if (!errorsVal) {
      setLoading(true);
      API(baseUrl)
        .post(endpoint, formData)
        .then((response) => {
          setLoading(false);
          setShowError(false);
          setUserName(response.data.data.name);
          const allPermissions = response.data.data.permission;
          const isSuperAdmin: boolean = response.data.data?.is_super_admin;
          if (response.data.data) {
            if (isSuperAdmin) {
              allPermissions.push({
                module: 'system-admin',
                data: ['create', 'update', 'view', 'delete'],
                id: null,
              });
              localStorage.setItem('is_super_admin', JSON.stringify(isSuperAdmin));
            }
            localStorage.setItem('permissions', JSON.stringify(response.data.data.permission));

            localStorage.setItem('token', JSON.stringify(response.data.data.token));
            localStorage.setItem('user_id', response.data.data.user_id);
            localStorage.setItem('team_name', JSON.stringify(response.data.data.team_name));
            localStorage.setItem(
              'configurations',
              JSON.stringify(response.data.data.configuration),
            );
            localStorage.setItem(
              'user_details',
              JSON.stringify({
                name: response.data.data.name,
                role: response.data.data.role_name,
                team: response.data.data.team_name,
                roleId: response.data.data.role_id,
                teamId: response.data.data.team_id,
                account_id: response.data.data.account_id,
                ...(isSuperAdmin && { isSuperAdmin }),
              }),
            );
            localStorage.setItem('email', JSON.stringify(response.data.data.email));
          }
          setUser({ ...user, email: response?.data?.data?.email });
          setCurrentUser({
            ...currentUser,
            email: response?.data?.data?.email,
          });
          setSuccess(true);
          const parsedPermissions: {
            module: string;
            llm: Array<number>;
            data: ('create' | 'update' | 'view' | 'delete')[];
          }[] = response.data.data.permission;
          setIsLoggedIn(true);
          if (location.state?.from) {
            navigate(location.state.from);
          } else {
            redirectToFirstPermittedModule(isSuperAdmin, parsedPermissions, navigate);
          }
          setUserdata({
            name: '',
            email: '',
            password: '',
          });
        })
        .catch((error) => {
          const err = !error?.response?.data?.message
            ? error?.response?.data
            : error?.response?.data?.message;
          const formattedError =
            typeof err === 'string' && err.length > 0
              ? err.charAt(0).toUpperCase() + err.substring(1)
              : 'Please try after sometime!';
          setErrMsg(formattedError);
          setLoading(false);
          setShowError(true);
          document.getElementsByTagName('body')[0].classList.add('stop-scroll');
        });
    }
  };
  const handleEyeClick = () => {
    setIsVisible(!isVisible);
  };

  return (
    <>
      <section className="flex flex-col max-w-[414px]">
        <form
          className="form-section bg-no-repeat bg-cover mr-0 ml-0 bg-[#f5f5f5] mt-4 h-full items-end justify-center py-[1.5rem] px-[1.875rem] rounded-xl border-[3px] border-[#A8C5DA] gap-[1.25rem]"
          onSubmit={handleSubmit}
        >
          <span className="font-bold text-3xl mb-[5px]">{WelcomeLabelText}</span>
          <br />
          <span className="text-[#6A6A6A] text-sm mb-3">{WelcomeText}</span>
          <InputField
            label="Email"
            type="text"
            tooltip="Allows only valid emails"
            placeholder="Example@domain.com"
            name="email"
            handleChange={handleFieldChange}
            error={errorInfo?.email || ''}
          />
          <div className="relative ">
            <InputField
              label="Password"
              type={isVisible ? 'text' : 'password'}
              placeholder="••••••••••"
              tooltip="min char 6, 1 letter, 1 number, 1 special char"
              name="password"
              handleChange={handleFieldChange}
              error={errorInfo?.password || ''}
            />
            <button
              type="button"
              onClick={handleEyeClick}
              className="cursor-pointer absolute top-10 right-3"
            >
              {isVisible === true ? (
                <img src={PasswordEyeIconGray} alt="show password" />
              ) : (
                <img src={PasswordEyeIconGraySlash} alt="hide password" />
              )}
            </button>
          </div>

          <p className="text-[#0d85bd] text-xs font-bold mb-2 text-end">
            <button
              type="button"
              className="cursor-pointer hover:underline"
              onClick={() => setForgotPasswordOpen(true)}
            >
              {ForgotPasswordText}
            </button>
          </p>
          {/* <p className="text-sm text-[#6A6A6A] text-center mt-4">
            {RegisteredText}{' '}
            <button
              type="button"
              className="text-sm font-bold text-[#F48B00] cursor-pointer hover:underline bg-transparent border-none p-0"
              onClick={() => setSignUpPageOpen(true)}
            >
              {SignUpText}
            </button>
          </p> */}
          <button
            type="submit"
            className={
              userdata?.email === '' || userdata?.password === ''
                ? 'bg-[#ccc] pointer-events-none text-[#fff] rounded-lg py-[12px] px-[60px] select-none w-full mt-4'
                : 'bg-[#0d85bd] text-[#fff] rounded-lg py-[12px] px-[60px] select-none w-full mt-4'
            }
          >
            {SubmitText}
          </button>
        </form>
      </section>
      {loading && createPortal(<LoaderOverlay />, document.body)}
      {showError &&
        createPortal(
          <Modal
            heading="Error"
            subHeading={errMsg}
            onClose={() => {
              document.getElementsByTagName('body')[0].classList.remove('stop-scroll');
              setShowError(false);
            }}
          />,
          document.body,
        )}
    </>
  );
}

export default AuthForm;
